import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import Latex from 'react-latex';
import { forEach, cloneDeep, findIndex, max, sortBy } from 'lodash';

import StackedLayout from '../../layouts/StackedLayout';
import EmptyData from '../../components/EmptyData';
import { PlusButton, ActionButton } from '../../components/buttons';
import QuestionTypeTabs from '../../components/ContentTabs';
import { ActionMenu, MenuItem } from '../../components/ActionMenu';
import ConfirmModal from '../../components/modals/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import ContentPicker from '../../components/ContentPicker';
import ExamQuestionView from './components/ExamQuestionView';
import TextPanel from '../../components/TextPanel';
import { getExamWithQuestions, addQuestion, removeQuestion, addCombinedMcq, removeCombinedMcq } from '../../services/exam';
import { CheckCircleIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/solid';
import { alphabet } from '../../utils';


const actionItems = [
  { name: 'Single MCQ', value: 'mcq', Icon: ClipboardDocumentListIcon },
  { name: 'Combined MCQ', value: 'combinedMcq', Icon: ClipboardDocumentListIcon }
]

export default function MCQExamQuestionsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const examId = params.id;
  const [subjectNames, setSubjectNames] = useState([]);
  const [exam, setExam] = useState(null);
  const [selectedSubjectIndex, setSelectedSubjectIndex] = useState(0);
  const [selectedSubjectName, setSelectedSubjectName] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [questionSetForSubject, setQuestionSetForSubject] = useState([]);
  const [combinedMcqSetForSubject, setCombinedMcqSetForSubject] = useState([]);
  const [mcqSetForSubject, setMcqSetForSubject] = useState([]);
  const [questionsForType, setQuestionsForType] = useState([]);
  const [comprehensionForType, setComprehensionForType] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState('A');
  const [newQuestionType, setNewQuestionType] = useState(null);
  const [selectedQuestion, setSelectdQuestion] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [breadcrumbPages, setBreadcrumbPages] = useState([]);
  const [openContentPicker, setOpenContentPicker] = useState(false);
  const [openQuestionView, setOpenQuestionView] = useState(false);
  const [showRemoveModel, setShowRemoveModal] = useState(false);
  const [initialState, setInitialState] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function _fetchInitialData() {
      try {
        const _exam = await getExamWithQuestions(examId);
        if (!_exam) { return navigate(-1); }

        let _subjectNames = []
        forEach(_exam.subjects, subject => {
          _subjectNames.push(subject.name);
        });
        const _selectedSubjectName = _subjectNames[selectedSubjectIndex];

        setExam(_exam);
        setSubjectNames(_subjectNames);
        setSelectedSubjectName(_selectedSubjectName);

        const listUrl = _exam.folder ? `/contents/exams/mcq/folder/${_exam.folder}` : `/contents/exams/mcq`;
        setBreadcrumbPages([
          { name: 'Exams', href: listUrl, current: false },
          { name: _exam?.name, current: true },
          { name: 'Questions', current: true }
        ]);
      } catch (error) {
        navigate(-1);
      }
    }
    _fetchInitialData();
    setLoading(false);
  }, []);

  useEffect(() => {
    async function _formatData() {

      const _mcqSetForSubject = exam?.subjects[selectedSubjectIndex]?.questionSet || [];
      setMcqSetForSubject(_mcqSetForSubject);

      const _combinedMcqSetForSubject = exam?.subjects[selectedSubjectIndex]?.combinedMcqSet || [];
      setCombinedMcqSetForSubject(_combinedMcqSetForSubject);

      const _questionSetForSubject = _mcqSetForSubject.concat(_combinedMcqSetForSubject);
      setQuestionSetForSubject(_questionSetForSubject);

      const _types = _questionSetForSubject?.length > 0 ? sortBy(_questionSetForSubject.map(q => q.type)) : ['A'];
      setSelectedQuestionType(_types[_types.length - 1]);
      setInitialState(false);
    }
    _formatData();
  }, [
    exam,
    selectedSubjectName,
  ]);

  useEffect(() => {
    const typeIndex = findIndex(questionSetForSubject, qs => qs.type === selectedQuestionType);
    setQuestionsForType(() => questionSetForSubject[typeIndex]?.combinedMcqs?.[0]?.questionSet || questionSetForSubject[typeIndex]?.questions || []);
    setComprehensionForType(() => questionSetForSubject[typeIndex]?.combinedMcqs?.[0]?.comprehension || null);

    formatTabs(questionSetForSubject);
  }, [
    selectedQuestionType,
    questionSetForSubject
  ])

  const formatTabs = () => {
    let _tabs = [];
    const _types = questionSetForSubject?.length > 0 ? sortBy(questionSetForSubject.map(q => q.type)) : ['A'];
    forEach(_types, (_type) => {
      const typeIndex = findIndex(questionSetForSubject, qs => qs.type === _type);
      const questionsCount = questionSetForSubject[typeIndex]?.combinedMcqs?.[0]?.questionSet?.length || questionSetForSubject[typeIndex]?.questions?.length || 0;
      _tabs.push({ name: _type, count: questionsCount });
    });

    setTabs(_tabs);
    setQuestionTypes(_types);
  };

  const handleSubjectSelect = (event, index) => {
    event.preventDefault();
    const _selectedSubjectName = subjectNames[index];
    setSelectedSubjectIndex(index);
    setSelectedSubjectName(_selectedSubjectName);
  };

  const handleTypeChange = (tabIndex) => {
    const selectedTab = tabs[tabIndex];
    const selectedType = selectedTab.name;
    setSelectedQuestionType(selectedType);
  }

  const handleAddQuestion = async (event, question) => {
    try {
      event.preventDefault();
      const _questionType = newQuestionType || selectedQuestionType || 'A';
      const payload = {
        subject: selectedSubjectName,
        questionType: _questionType,
        questionId: question._id
      };

      let updatedExam;
      if (contentType === 'mcq') {
        updatedExam = await addQuestion(exam._id, payload);
      } else if (contentType === 'combinedMcq') {
        updatedExam = await addCombinedMcq(exam._id, payload);
      }
      setSelectedQuestionType(_questionType);
      setNewQuestionType(null);

      const _mcqSetForSubject = updatedExam?.questionSet || [];
      setMcqSetForSubject(_mcqSetForSubject);

      const _combinedMcqSetForSubject = updatedExam?.combinedMcqSet || [];
      setCombinedMcqSetForSubject(_combinedMcqSetForSubject);

      const _questionSetForSubject = _mcqSetForSubject.concat(_combinedMcqSetForSubject);
      setQuestionSetForSubject(_questionSetForSubject);
      toast.success('Question added successfully.');

      let _exam = cloneDeep(exam);
      _exam.subjects[selectedSubjectIndex].questionSet = updatedExam.questionSet;
      _exam.subjects[selectedSubjectIndex].combinedMcqSet = updatedExam.combinedMcqSet;
      setExam(_exam);
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const handleRemoveQuestion = async (event) => {
    try {
      event.preventDefault();
      const payload = {
        subject: selectedSubjectName,
        questionType: selectedQuestionType,
        questionId: selectedQuestion._id
      };

      let updatedExam;
      if (comprehensionForType) {
        updatedExam = await removeCombinedMcq(exam._id, payload);
      } else {
        updatedExam = await removeQuestion(exam._id, payload);
      }

      const currentTypeIndex = findIndex(questionTypes, t => t === selectedQuestionType);
      if (comprehensionForType || tabs[currentTypeIndex].count === 1) {
        const totalTypes = questionTypes.length;
        const nextTypeIndex = currentTypeIndex === totalTypes - 1 ? currentTypeIndex - 1 : currentTypeIndex + 1;
        setSelectedQuestionType(questionTypes[nextTypeIndex]);
      }

      const _mcqSetForSubject = updatedExam?.questionSet || [];
      setMcqSetForSubject(_mcqSetForSubject);

      const _combinedMcqSetForSubject = updatedExam?.combinedMcqSet || [];
      setCombinedMcqSetForSubject(_combinedMcqSetForSubject);

      const _questionSetForSubject = _mcqSetForSubject.concat(_combinedMcqSetForSubject);
      setQuestionSetForSubject(_questionSetForSubject);
      toast.success('Question removed successfully.');

      let _exam = cloneDeep(exam);
      _exam.subjects[selectedSubjectIndex].questionSet = updatedExam.questionSet;
      _exam.subjects[selectedSubjectIndex].combinedMcqSet = updatedExam.combinedMcqSet;
      setExam(_exam);
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const addNewType = (contentType) => {
    const lastType = max(questionTypes);
    const nextType = String.fromCharCode(lastType.charCodeAt(0) + 1);
    setNewQuestionType(nextType);
    setContentType(contentType);
    setOpenContentPicker(true);
  };

  const getSelectedQuestions = () => {
    let selectedQuestions = [];
    forEach(questionSetForSubject, qs => {
      forEach(qs.questions, q => selectedQuestions.push(q._id));
      forEach(qs.combinedMcqs, q => selectedQuestions.push(q._id));
    });
    return selectedQuestions
  };

  return (
    <StackedLayout
      loading={loading}
      sidebarNavigation={subjectNames}
      currentSidebarNavigation={selectedSubjectName}
      onNavigationChange={handleSubjectSelect}
    >
      <Breadcrumb pages={breadcrumbPages} />
      <div>
        {openContentPicker &&
          <ContentPicker
            title="Select questions"
            contentType={contentType}
            selectedContents={getSelectedQuestions()}
            onSelect={handleAddQuestion}
            onCancel={() => {
              setOpenContentPicker(false);
              setNewQuestionType(null);
            }}
          />}

        {openQuestionView &&
          <ExamQuestionView
            question={selectedQuestion}
            onClose={() => {
              setSelectdQuestion(null);
              setOpenQuestionView(false);
            }}
          />}

        {showRemoveModel &&
          <ConfirmModal
            title="Remove question"
            description="Are you sure to remove this question from this exam?"
            onConfirm={handleRemoveQuestion}
            onCancel={() => {
              setSelectdQuestion(null);
              setShowRemoveModal(false);
            }}
          />}

        {questionSetForSubject.length > 0 &&
          <QuestionTypeTabs
            tabs={tabs}
            contentType="question"
            selectedTab={selectedQuestionType}
            onTabChange={handleTypeChange}
          >
            {questionsForType?.length > 0 &&
              <div>
                <ActionButton
                  label="New type"
                  items={actionItems}
                  onClick={(item) => {
                    addNewType(item.value);
                  }}
                />
              </div>}
          </QuestionTypeTabs>}

        {questionsForType.length === 0 &&
          <EmptyData
            title="No questions"
            subTitle="Add questions to this subject."
            hideNewButton={true}
            action="Add questions"
            onCreate={() => setOpenContentPicker(true)}
          >
            <div>
              <ActionButton
                label="Add questions"
                items={actionItems}
                onClick={(item) => {
                  setContentType(item.value);
                  setOpenContentPicker(true);
                }}
              />
            </div>
          </EmptyData>
        }

        {comprehensionForType &&
          <div className="flex">
            <TextPanel text={comprehensionForType} />
            <div className="flex shrink-0 items-center gap-x-4">
              <ActionItems
                question={combinedMcqSetForSubject[0]?.combinedMcqs[0]}
                onRemove={() => {
                  setSelectdQuestion(combinedMcqSetForSubject[0]?.combinedMcqs[0]);
                  setShowRemoveModal(true);
                }}
              />
            </div>
          </div>
        }

        <ul className="mx-2 mb-4 divide-y divide-gray-100">
          {questionsForType.map((question, index) => (
            <li key={question._id || `question-index[${index}]`}>
              <div className="flex justify-between gap-x-6 py-5 cursor-pointer">
                <div className="flex w-2/3 gap-x-4">
                  <span className="flex-none text-sm text-gray-900">
                    {`${index + 1}. `}
                  </span>
                  <div className="min-w-0 flex-auto">

                    <span className="questions-answer-wrapper text-md leading-6 text-gray-900">
                      <TextPanel text={question?.question} />
                    </span>
                  </div>
                </div>
                {!comprehensionForType &&
                  <div className="flex shrink-0 items-center gap-x-4">
                    <ActionItems
                      question={question}
                      onRemove={() => {
                        setSelectdQuestion(question);
                        setShowRemoveModal(true);
                      }}
                    />
                  </div>}
              </div>

              <div className="flex gap-x-4">
                {question?.answers?.map((answer, answerIndex) => (
                  <span className="">
                    <span className="flex text-sm items-center text-gray-900">
                      <span>{`${alphabet[answerIndex]}.`}</span>&nbsp;
                      <span><TextPanel text={answer.value} /></span>&nbsp;
                      {answer.isCorrect && <CheckCircleIcon className="h-4 w-4 text-green-600" />}
                    </span>
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
        {!comprehensionForType && questionsForType.length > 0 &&
          <PlusButton
            label="Add question"
            onClick={() => setOpenContentPicker(true)}
          />}
      </div>
    </StackedLayout>
  )
};

function ActionItems(props) {
  const { question, onRemove } = props;

  let folderDestination;
  if (question.comprehension) {
    folderDestination = question?.folder ? `/contents/questions/combined-mcq/folder/${question?.folder}` : `/contents/questions/combined-mcq`
  } else {
    folderDestination = question?.folder ? `/contents/questions/mcq/folder/${question?.folder}` : `/contents/questions/mcq`
  }
  return (
    <ActionMenu>
      <div className="py-1">
        <MenuItem label="Go to question folder" href={folderDestination} />
      </div>

      <div className="py-1">
        <MenuItem label="Remove" isDanger={true} onClick={onRemove} />
      </div>
    </ActionMenu>
  )
};
